import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { enqueueSnackbar } from 'notistack';

class ApiService {
  private client: AxiosInstance;
  private refreshPromise: Promise<string | null> | null = null;

  constructor() {
    const baseURL = this.getBaseUrl();
    this.client = axios.create({
      baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor
    this.client.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const token = localStorage.getItem('access_token');
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error: AxiosError) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

        const responseData = error.response?.data as any;
        const requestUrl = originalRequest.url || '';
        // Only skip refresh flow for endpoints that are part of login/refresh itself.
        const isNoRefreshAuthRequest =
          requestUrl.includes('/auth/login/') ||
          requestUrl.includes('/auth/register/') ||
          requestUrl.includes('/auth/token/refresh/');
        const isTwoFactorRequired = !!responseData?.two_factor_required;

        if (error.response?.status === 401 && !originalRequest._retry && !isNoRefreshAuthRequest && !isTwoFactorRequired) {
          originalRequest._retry = true;
          
          try {
            const access = await this.refreshAccessToken();
            if (access) {
              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${access}`;
              }

              return this.client(originalRequest);
            }
          } catch (refreshError) {
            // Refresh failed — stale/invalid tokens. Clear them and send user to login.
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            window.location.href = '/login';
            return Promise.reject(refreshError);
          }
        }
        
        // Don't redirect on 403 (permission denied) - just show error
        if (error.response?.status === 403) {
          const errorData = error.response.data as any;
          const errorMessage = errorData.detail || errorData.error || errorData.message || 'Permission denied';
          enqueueSnackbar(errorMessage, { variant: 'error' });
          return Promise.reject(error);
        }

        // Handle specific error messages (but don't show for 401/403 as they're handled above)
        if (error.response?.data && error.response.status !== 401 && error.response.status !== 403) {
          const errorData = error.response.data as any;
          const errorMessage = errorData.detail || errorData.error || errorData.message || 'An error occurred';
          enqueueSnackbar(errorMessage, { variant: 'error' });
        } else if (error.request && !error.response) {
          enqueueSnackbar('Network error. Please check your connection.', { variant: 'error' });
        }

        return Promise.reject(error);
      }
    );
  }

  private async refreshAccessToken(): Promise<string | null> {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      return null;
    }

    // Single-flight refresh to prevent concurrent refresh races on bursty 401s.
    if (!this.refreshPromise) {
      const baseURL = this.getBaseUrl();
      this.refreshPromise = axios
        .post<{ access: string }>(`${baseURL}/auth/token/refresh/`, { refresh: refreshToken })
        .then((response) => {
          const access = response?.data?.access;
          if (access) {
            localStorage.setItem('access_token', access);
            return access;
          }
          throw new Error('Refresh response missing access token');
        })
        .finally(() => {
          this.refreshPromise = null;
        });
    }

    return this.refreshPromise;
  }

  private getBaseUrl() {
    const envUrl = (process.env.REACT_APP_API_URL || '').trim();
    if (envUrl) {
      return envUrl.replace(/\/+$/, '');
    }
    const origin = window.location.origin.replace(/\/+$/, '');
    const hostname = window.location.hostname;

    // Check for local development
    const isLocalDev = hostname === 'localhost' || hostname === '127.0.0.1';
    if (isLocalDev) {
      return 'http://127.0.0.1:8000/api';
    }

    // For production domains, use same-origin API
    if (hostname === 'werk-gang.com' || hostname === 'w.o-d-a.net' || hostname === 'ai.werk-gang.com') {
      return `${origin}/api`;
    }

    // Default to same-origin API in production (avoids localhost in deployed builds)
    return `${origin}/api`;
  }

  async get<T>(url: string, params?: any): Promise<T> {
    // Force a fresh response — some browsers (Chrome in particular) will
    // heuristically cache XHR GETs that lack explicit Cache-Control, which
    // can make the workflow editor look like a save was rolled back.
    const response = await this.client.get(url, {
      params,
      headers: {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      },
    });
    return response.data;
  }

  async post<T>(url: string, data?: any, options?: { timeout?: number }): Promise<T> {
    const response = await this.client.post(url, data, options);
    return response.data;
  }

  async put<T>(url: string, data?: any): Promise<T> {
    const response = await this.client.put(url, data);
    return response.data;
  }

  async patch<T>(url: string, data?: any): Promise<T> {
    const response = await this.client.patch(url, data);
    return response.data;
  }

  async delete<T>(url: string): Promise<T> {
    const response = await this.client.delete(url);
    return response.data;
  }

  async upload<T>(url: string, file: File, fieldName: string = 'file'): Promise<T> {
    const formData = new FormData();
    formData.append(fieldName, file);

    const response = await this.client.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  }
}

export const apiService = new ApiService();